import { API_URL } from '@/config/constants'
import axios from 'axios'

/**
 * Chat file upload response format
 */
export interface ChatFileUploadResponse {
  id: string
  content?: string
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
  messageType: 'file' | 'image'
  senderId: string
  conversationId: string
  createdAt: string
  sender: {
    id: string
    firstname: string
    lastname: string
  }
}

/**
 * Service for handling file uploads in chat messages
 */
export class ChatFileUploadService {
  /**
   * Upload a file as part of a chat message
   * @param file The file to upload
   * @param conversationId The conversation ID
   * @param content Optional text message to accompany the file
   * @returns The chat message with file information
   */
  async uploadFileMessage(
    file: File,
    conversationId: string,
    content: string = ''
  ): Promise<ChatFileUploadResponse> {
    try {
      // Check file size - maximum 10MB for chat files
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File too large. Maximum size is 10MB')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('conversationId', conversationId)
      if (content.trim()) {
        formData.append('content', content.trim())
      }

      // console.log('Uploading file to chat:', {
      //   fileName: file.name,
      //   fileSize: file.size,
      //   conversationId,
      //   hasContent: !!content.trim()
      // })

      const response = await axios.post(
        `${API_URL}/chat/messages/with-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          timeout: 30000, // 30 second timeout for file uploads
        }
      )

      if (response.status >= 200 && response.status < 300 && response.data) {
        // console.log('Chat file upload successful:', response.data)
        return response.data
      }

      throw new Error(`Upload failed with status ${response.status}`)
    } catch (error: any) {
      // console.error('Error uploading file to chat:', error)
      
      // Log detailed error information for debugging
      if (error.response) {
        // console.error('Error response status:', error.response.status)
        // console.error('Error response data:', error.response.data)
        // console.error('Error response headers:', error.response.headers)
      }

      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.')
      }
      
      if (error.response?.status === 403) {
        throw new Error('You are not authorized to send files in this conversation.')
      }
      
      if (error.response?.status === 404) {
        throw new Error('Conversation not found.')
      }
      
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid file or request'
        throw new Error(errorMessage)
      }
      
      // Handle 500 Internal Server Error with detailed message
      if (error.response?.status === 500) {
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           'Server error occurred while uploading file. Please check backend logs.'
        // console.error('Server error details:', {
        //   message: errorMessage,
        //   data: error.response.data,
        //   stack: error.response.data?.stack
        // })
        throw new Error(`Server error: ${errorMessage}`)
      }

      // Handle network errors
      if (error.message?.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again.')
      }

      // Handle timeout errors
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timeout. Please try again with a smaller file.')
      }

      throw new Error(error.message || 'Failed to upload file')
    }
  }

  /**
   * Get the appropriate message type based on file MIME type
   * @param mimeType The MIME type of the file
   * @returns 'image' for image files, 'file' for others
   */
  getMessageType(mimeType: string): 'image' | 'file' {
    return mimeType.startsWith('image/') ? 'image' : 'file'
  }

  /**
   * Format file size for display
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Check if a file type is supported for chat uploads
   * @param file The file to check
   * @returns true if the file type is supported
   */
  isSupportedFileType(file: File): boolean {
    const supportedTypes = [
      // Images
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      // Text files
      'text/plain',
      'text/csv',
    ]

    return supportedTypes.includes(file.type)
  }

  /**
   * Get file icon based on MIME type
   * @param mimeType The MIME type of the file
   * @returns Icon name for the file type
   */
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType === 'application/pdf') return 'file-text'
    if (mimeType.includes('word') || mimeType.includes('document')) return 'file-text'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'file-spreadsheet'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'presentation'
    if (mimeType.startsWith('text/')) return 'file-text'
    return 'file'
  }
}

export default new ChatFileUploadService()